import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useAuth } from "../../auth/AuthContext";
import {
  IncomeMonthRow,
  IncomeReport,
  IncomeWeekRow,
  IncomeWeekTransactionCount,
  IncomeYearRow,
  getIncomeReport,
} from "../../api";
import Price from "../widgets/Price";
import RoughLine, { LineSeries } from "../widgets/charts/RoughLine";

type LoadState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; report: IncomeReport };

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const IncomeReportPage = () => {
  const { idToken } = useAuth();
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    if (!idToken) return;
    let cancelled = false;
    setState({ kind: "loading" });
    getIncomeReport(idToken)
      .then((report) => {
        if (!cancelled) setState({ kind: "ready", report });
      })
      .catch((err) => {
        if (!cancelled) {
          setState({
            kind: "error",
            message: (err as Error)?.message || "failed to load income report",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [idToken]);

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, color: "#1f2937", mb: 0.5 }}
      >
        Income
      </Typography>
      <Typography variant="body2" sx={{ color: "#64748b", mb: 3 }}>
        Sales across in-store transactions AND fulfilled web orders (excluding
        test orders). Subtotal is pre-tax; total is post-tax. Tax for any row is
        the difference.
      </Typography>

      {state.kind === "loading" && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {state.kind === "error" && (
        <Alert severity="error">{state.message}</Alert>
      )}

      {state.kind === "ready" && (
        <Stack spacing={4}>
          <WeeklyChart report={state.report} />
          <CumulativeWeeklyChart report={state.report} />
          <WeeklyTransactionCountChart report={state.report} />
          <YearlyTable years={state.report.years} />
          <MonthlyTable report={state.report} />
        </Stack>
      )}
    </Box>
  );
};

const YearlyTable = ({ years }: { years: IncomeYearRow[] }) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Typography
      variant="h6"
      sx={{ fontWeight: 600, color: "#1f2937", mb: 1.5 }}
    >
      Yearly totals
    </Typography>
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Year</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              Subtotal
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              Total
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              Transactions
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              Orders
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {years.map((y) => (
            <TableRow key={y.label}>
              <TableCell>{y.label}</TableCell>
              <TableCell align="right">
                <Price price={y.subtotal} showZero />
              </TableCell>
              <TableCell align="right">
                <Price price={y.total} showZero />
              </TableCell>
              <TableCell align="right">
                {y.transaction_count.toLocaleString()}
              </TableCell>
              <TableCell align="right">
                {y.order_count.toLocaleString()}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
);

// Pivot the sparse month list into a 12-row table with one column per
// year. Missing (year, month) combos render as blank cells.
const MonthlyTable = ({ report }: { report: IncomeReport }) => {
  const years: number[] = [];
  for (let y = report.start_year; y <= report.end_year; y++) years.push(y);

  const byYearMonth = new Map<string, IncomeMonthRow>();
  for (const row of report.months) {
    byYearMonth.set(`${row.year}-${row.month}`, row);
  }
  const cell = (year: number, month: number) =>
    byYearMonth.get(`${year}-${month}`);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography
        variant="h6"
        sx={{ fontWeight: 600, color: "#1f2937", mb: 1.5 }}
      >
        Monthly breakdown
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Month</TableCell>
              {years.map((y) => (
                <TableCell
                  key={`sub-${y}`}
                  align="right"
                  sx={{ fontWeight: 600 }}
                >
                  {y} Subtotal
                </TableCell>
              ))}
              {years.map((y) => (
                <TableCell
                  key={`tot-${y}`}
                  align="right"
                  sx={{ fontWeight: 600 }}
                >
                  {y} Total
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {MONTH_LABELS.map((label, idx) => {
              const month = idx + 1;
              return (
                <TableRow key={month}>
                  <TableCell>{label}</TableCell>
                  {years.map((y) => {
                    const r = cell(y, month);
                    return (
                      <TableCell key={`sub-${y}-${month}`} align="right">
                        {r ? <Price price={r.subtotal} showZero /> : ""}
                      </TableCell>
                    );
                  })}
                  {years.map((y) => {
                    const r = cell(y, month);
                    return (
                      <TableCell key={`tot-${y}-${month}`} align="right">
                        {r ? <Price price={r.total} showZero /> : ""}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

// Year-over-year weekly subtotal — bridgewater only. Each year is a
// distinct line drawn over the same week-of-year x-axis. ISO weeks
// can run 1..53 in years where Jan 1 is a Thursday (or a Wednesday in
// a leap year); we keep all 53 slots so end-of-year activity in those
// years still lands in the chart instead of getting silently dropped.
// Missing weeks come through as 0 and the per-series trim drops
// trailing zeros so the line ends at the last week with real data.
const WEEK_LABELS: string[] = Array.from({ length: 53 }, (_, i) => `${i + 1}`);

// One distinct colour per year line, ordered by age (index 0 = first
// year in the window). Muted palette — saturated brights were rough on
// the eyes side-by-side. Cycle through if we ever widen the window
// beyond what's listed here.
const YEAR_COLORS = [
  "#7a1f2b", // 2025 — maroon
  "#3b6fb0", // 2026 — softer slate blue
  "#5d6b3a", // olive (3rd year, if added)
  "#6b4c8a", // muted purple (4th)
];

const WeeklyChart = ({ report }: { report: IncomeReport }) => {
  // Defensive fallback for the (rare) window when frontend has shipped
  // the weekly chart but the backend still serves the older response
  // without `weeks`. Strip the guard once both sides have rolled.
  const weeks = report.weeks ?? [];
  if (weeks.length === 0) return null;

  const years: number[] = [];
  for (let y = report.start_year; y <= report.end_year; y++) years.push(y);

  const byYearWeek = new Map<string, IncomeWeekRow>();
  for (const row of weeks) {
    byYearWeek.set(`${row.year}-${row.week}`, row);
  }

  const series: LineSeries[] = years.map((y) => {
    // Build the full 52-week vector (cents → dollars, 0 for missing
    // weeks), then trim the trailing zeros so the line ends at the
    // last week with real data instead of plunging to zero for the
    // rest of the year (useful for the partial current year). The
    // x-axis still spans 1..52 because xLabels is fixed across series.
    const full = WEEK_LABELS.map((_, i) => {
      const r = byYearWeek.get(`${y}-${i + 1}`);
      return r ? Math.round(r.subtotal) / 100 : 0;
    });
    let lastNonZero = -1;
    for (let i = full.length - 1; i >= 0; i--) {
      if (full[i] > 0) {
        lastNonZero = i;
        break;
      }
    }
    // Series name doubles as a CSS class via rough-viz's interaction
    // selectors (`.${name}classtext`). It must be a single CSS
    // identifier: no leading digit, no whitespace. `FY2025` clears
    // both bars while still reading as the year.
    return {
      name: `FY${y}`,
      values: lastNonZero >= 0 ? full.slice(0, lastNonZero + 1) : [],
    };
  });

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography
        variant="h6"
        sx={{ fontWeight: 600, color: "#1f2937", mb: 0.5 }}
      >
        Weekly subtotal — Bridgewater
      </Typography>
      <Typography variant="body2" sx={{ color: "#64748b", mb: 2 }}>
        In-store subtotal by ISO week-of-year for the Bridgewater shop. One line
        per year; dollars, pre-tax.
      </Typography>
      <RoughLine
        xLabels={WEEK_LABELS}
        series={series}
        xLabel="Week of year"
        yLabel="Subtotal ($)"
        colors={years.map((_, i) => YEAR_COLORS[i % YEAR_COLORS.length])}
        roughness={1}
        fontStyle={1}
        width={1100}
        height={420}
        bulletRadius={5}
        // SI-suffix format ("5k", "12.3k", "1.2M") — keeps y-axis
        // labels short so the axis title doesn't collide with them.
        // The hover popups still show the full pre-tax dollar amount.
        yValueFormat="~s"
        axisFontSize="1rem"
        tooltipFontSize="1.25rem"
      />
    </Paper>
  );
};

// Same source data as `WeeklyChart`, but each point is the YTD running
// total through that ISO week — so each line creeps up across the year
// instead of fluctuating week to week. Useful for comparing pace
// year-over-year (which year is ahead at any given week).
const CumulativeWeeklyChart = ({ report }: { report: IncomeReport }) => {
  const weeks = report.weeks ?? [];
  if (weeks.length === 0) return null;

  const years: number[] = [];
  for (let y = report.start_year; y <= report.end_year; y++) years.push(y);

  const byYearWeek = new Map<string, IncomeWeekRow>();
  for (const row of weeks) {
    byYearWeek.set(`${row.year}-${row.week}`, row);
  }

  const series: LineSeries[] = years.map((y) => {
    // Same per-week build + trailing-zero trim as `WeeklyChart` — once
    // we know the last week with real data, we accumulate up to that
    // point. Trailing zeros are dropped so the partial current year
    // ends at "now" instead of plateauing flat to week 53.
    const full = WEEK_LABELS.map((_, i) => {
      const r = byYearWeek.get(`${y}-${i + 1}`);
      return r ? Math.round(r.subtotal) / 100 : 0;
    });
    let lastNonZero = -1;
    for (let i = full.length - 1; i >= 0; i--) {
      if (full[i] > 0) {
        lastNonZero = i;
        break;
      }
    }
    const truncated = lastNonZero >= 0 ? full.slice(0, lastNonZero + 1) : [];
    const cumulative: number[] = [];
    let running = 0;
    for (const v of truncated) {
      running += v;
      cumulative.push(running);
    }
    return {
      name: `FY${y}`,
      values: cumulative,
    };
  });

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography
        variant="h6"
        sx={{ fontWeight: 600, color: "#1f2937", mb: 0.5 }}
      >
        Cumulative subtotal — Bridgewater
      </Typography>
      <Typography variant="body2" sx={{ color: "#64748b", mb: 2 }}>
        Year-to-date running total by ISO week. One line per year; dollars,
        pre-tax.
      </Typography>
      <RoughLine
        xLabels={WEEK_LABELS}
        series={series}
        xLabel="Week of year"
        yLabel="YTD subtotal ($)"
        colors={years.map((_, i) => YEAR_COLORS[i % YEAR_COLORS.length])}
        roughness={1}
        fontStyle={1}
        width={1100}
        height={420}
        bulletRadius={5}
        // SI-suffix format ("5k", "12.3k", "1.2M") — keeps y-axis
        // labels short so the axis title doesn't collide with them.
        // The hover popups still show the full pre-tax dollar amount.
        yValueFormat="~s"
        axisFontSize="1rem"
        tooltipFontSize="1.25rem"
      />
    </Paper>
  );
};

// Per-week count of *transactions* (in-store across every location +
// fulfilled web orders). Different shape from the subtotal charts —
// this is a raw count, not money, and includes online sales because
// they're real customer-facing transactions even though they're not
// tied to a physical location.
const WeeklyTransactionCountChart = ({ report }: { report: IncomeReport }) => {
  const rows = report.weeks_transactions ?? [];
  if (rows.length === 0) return null;

  const years: number[] = [];
  for (let y = report.start_year; y <= report.end_year; y++) years.push(y);

  const byYearWeek = new Map<string, IncomeWeekTransactionCount>();
  for (const row of rows) {
    byYearWeek.set(`${row.year}-${row.week}`, row);
  }

  const series: LineSeries[] = years.map((y) => {
    const full = WEEK_LABELS.map((_, i) => {
      const r = byYearWeek.get(`${y}-${i + 1}`);
      return r ? r.in_store_count + r.order_count : 0;
    });
    let lastNonZero = -1;
    for (let i = full.length - 1; i >= 0; i--) {
      if (full[i] > 0) {
        lastNonZero = i;
        break;
      }
    }
    return {
      name: `FY${y}`,
      values: lastNonZero >= 0 ? full.slice(0, lastNonZero + 1) : [],
    };
  });

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography
        variant="h6"
        sx={{ fontWeight: 600, color: "#1f2937", mb: 0.5 }}
      >
        Weekly transaction count
      </Typography>
      <Typography variant="body2" sx={{ color: "#64748b", mb: 2 }}>
        Number of transactions per ISO week — Bridgewater in-store plus
        fulfilled web orders. Earlier years had multiple shops; this keeps the
        comparison apples-to-apples with the active business.
      </Typography>
      <RoughLine
        xLabels={WEEK_LABELS}
        series={series}
        xLabel="Week of year"
        yLabel="Transactions"
        colors={years.map((_, i) => YEAR_COLORS[i % YEAR_COLORS.length])}
        roughness={1}
        fontStyle={1}
        width={1100}
        height={420}
        bulletRadius={5}
        // Raw integer count — use ",d" so a tick like 1234 reads as
        // "1,234" without a stray decimal.
        yValueFormat=",d"
        axisFontSize="1rem"
        tooltipFontSize="1.25rem"
      />
    </Paper>
  );
};

export default IncomeReportPage;
